import { Component } from '@theme/component';

/**
 * SD Custom Section Carousel Component
 * Handles gradient swap animation and dot navigation
 *
 * @typedef {Object} SdCustomSectionRefs
 * @property {HTMLElement[]} slides - Carousel slide elements
 * @property {HTMLButtonElement[]} [dot] - Array of dot navigation buttons
 */

/**
 * @extends {Component<SdCustomSectionRefs>}
 */
class SdCustomSection extends Component {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    super.connectedCallback();
    this.#initGradientObserver();
    this.#initSlideshowListener();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cleanup();
  }

  /**
   * Initialize listener for slideshow select events to update dots and announce to screen readers
   */
  #initSlideshowListener() {
    // Listen on this element since slideshow:select bubbles up from slideshow-component
    this.addEventListener('slideshow:select', (event) => {
      const { index } = event.detail;
      this.#updateDots(index);
      this.#announceSlide(index);
    });
  }

  /**
   * Announce current slide to screen readers via live region
   * @param {number} index - Current slide index (0-based)
   */
  #announceSlide(index) {
    const liveRegion = this.querySelector('[ref="liveRegion"]');
    if (!liveRegion) return;

    const totalSlides = this.querySelectorAll('.sd-custom-section__slide').length;
    liveRegion.textContent = `Item ${index + 1} of ${totalSlides}`;
  }

  /**
   * Update dots to reflect current slide (same as sd-slider)
   * @param {number} index - Current slide index
   */
  #updateDots(index) {
    // Query dots directly since they're inside slideshow-component (refs don't bubble up)
    // Uses the sd-carousel-dots snippet class
    const dots = this.querySelectorAll('.sd-carousel-dots__dot');
    if (!dots.length) return;

    for (const [i, dot] of dots.entries()) {
      if (i === index) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    }
  }

  /**
   * Initialize the IntersectionObserver for gradient swap
   * Observes the last slide to toggle the --at-end class
   */
  #initGradientObserver() {
    const slides = this.querySelectorAll('.sd-custom-section__slide');
    const lastSlide = slides[slides.length - 1];

    if (!lastSlide) return;

    const scrollContainer = this.querySelector('slideshow-slides');
    if (!scrollContainer) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          this.classList.toggle('sd-custom-section--at-end', entry.isIntersecting);
        }
      },
      { threshold: 0.9, root: scrollContainer }
    );

    this.#observer.observe(lastSlide);
  }

  /**
   * Cleanup observer on disconnect
   */
  #cleanup() {
    if (this.#observer) {
      this.#observer.disconnect();
      this.#observer = null;
    }
  }
}

customElements.define('sd-custom-section', SdCustomSection);
